(() => {
  'use strict';
  if (window.LVNFootwear) { window.LVNFootwear.init(); return; }
  const cache = new Map();
  const collator = new Intl.Collator(undefined, { numeric: true, sensitivity: 'base' });
  const sorted = values => [...new Set(values)].sort(collator.compare);
  const node = (tag, className, text) => {
    const el = document.createElement(tag);
    if (className) el.className = className;
    if (text !== undefined) el.textContent = text;
    return el;
  };
  function collectionURL(path) {
    const url = new URL(path, location.origin);
    url.search = '';
    const preview = new URL(location.href).searchParams.get('preview_theme_id');
    if (preview) url.searchParams.set('preview_theme_id', preview);
    return url;
  }
  // Never infer US/EU conversions or invent size labels. Legacy Size is accepted
  // only when numeric; apparel accidentally included in the collection is excluded.
  function normalizeProduct(p) {
    let index = p.options.findIndex(o => /^shoe[\s_-]*size$/i.test(o.trim()));
    if (index < 0) {
      if (/\b(shorts|pants|trousers|jeans|shirt|tee|hoodie|jacket)\b/i.test(p.title)) return null;
      index = p.options.findIndex(o => /^size$/i.test(o.trim()));
      if (index < 0 || !p.variants.every(v => /^\d+(?:\.\d+)?$/.test(String(v.options[index]).trim()))) return null;
    }
    const variants = p.variants.map(v => ({ ...v, size: String(v.options[index]).trim() })).filter(v => v.size);
    return variants.length ? { ...p, variants } : null;
  }
  async function catalog(path) {
    const key = new URL(path, location.origin).pathname;
    if (!cache.has(key)) cache.set(key, (async () => {
      const products = new Map(), seen = new Set();
      let page = 1, expected = null, received = 0;
      while (page) {
        if (seen.has(page)) throw new Error('Repeated catalog page');
        seen.add(page);
        const url = collectionURL(path);
        url.searchParams.set('view', 'lvn-shoe-data');
        url.searchParams.set('page', page);
        const controller = new AbortController();
        const timeout = setTimeout(() => controller.abort(), 20000);
        let data;
        try {
          const response = await fetch(url, { credentials: 'same-origin', signal: controller.signal });
          if (!response.ok) throw new Error('Catalog unavailable');
          data = await response.json();
        } finally { clearTimeout(timeout); }
        if (!Array.isArray(data.products)) throw new Error('Invalid catalog');
        expected = data.total;
        received += data.products.length;
        data.products.forEach(p => { const shoe = normalizeProduct(p); if (shoe) products.set(p.id, shoe); });
        page = data.next_page;
      }
      if (received !== expected) throw new Error('Incomplete catalog');
      return [...products.values()];
    })().catch(error => { cache.delete(key); throw error; }));
    return cache.get(key);
  }
  async function menu(slot) {
    if (slot.dataset.shoeReady) return;
    slot.dataset.shoeReady = 'loading';
    try {
      const products = await catalog(slot.dataset.lvnFootwearSource);
      const sizes = sorted(products.flatMap(p => p.variants.map(v => v.size)));
      const fragment = document.createDocumentFragment();
      sizes.forEach(size => {
        const link = node('a', 'lvn-mega-chip', size);
        const url = collectionURL(slot.dataset.lvnFootwearSource);
        url.searchParams.set('lvn_size', size);
        link.href = url.pathname + url.search;
        fragment.append(link);
      });
      slot.replaceChildren(fragment);
      slot.classList.add('lvn-size-results');
      slot.dataset.shoeReady = 'true';
    } catch (error) {
      slot.dataset.shoeReady = '';
      slot.replaceChildren(node('span', 'lvn-size-error', slot.dataset.error));
      const retry = node('button', 'lvn-mega-chip', slot.dataset.retry);
      retry.type = 'button';
      retry.addEventListener('click', () => menu(slot), { once: true });
      slot.append(retry);
    }
  }
  async function browser(section) {
    if (section.dataset.shoeReady) return;
    section.dataset.shoeReady = 'loading';
    const find = name => section.querySelector(`[data-lvn-shoe-${name}]`);
    const grid = find('grid'), status = find('status'), retry = find('retry');
    retry.hidden = true;
    grid.setAttribute('aria-busy', 'true');
    try {
      const products = await catalog(section.dataset.collection);
      const sizes = sorted(products.flatMap(p => p.variants.map(v => v.size)));
      const sizeSelect = find('size'), stock = find('stock'), sort = find('sort');
      sizes.forEach(size => { const option = node('option', '', size); option.value = size; sizeSelect.append(option); });
      let page = 1;
      const pageSize = 24;
      function readState() {
        const params = new URL(location.href).searchParams;
        const size = params.get('lvn_size') || params.get('filter.v.option.shoe-size') || params.get('filter.v.option.size') || '';
        if (size && ![...sizeSelect.options].some(o => o.value === size)) {
          const option = node('option', '', size); option.value = size; sizeSelect.append(option);
        }
        sizeSelect.value = size;
        stock.checked = params.get('lvn_stock') === '1';
        sort.value = params.get('lvn_sort') || 'featured';
        if (!sort.value) sort.value = 'featured';
        page = Math.max(1, Number.parseInt(params.get('lvn_page'), 10) || 1);
      }
      function writeState() {
        const url = new URL(location.href);
        ['page', 'sort_by'].forEach(key => url.searchParams.delete(key));
        [...url.searchParams.keys()].filter(k => k.startsWith('filter.')).forEach(k => url.searchParams.delete(k));
        const values = { lvn_size: sizeSelect.value, lvn_stock: stock.checked ? '1' : '', lvn_sort: sort.value === 'featured' ? '' : sort.value, lvn_page: page > 1 ? String(page) : '' };
        Object.entries(values).forEach(([key, value]) => value ? url.searchParams.set(key, value) : url.searchParams.delete(key));
        history.pushState({}, '', url);
      }
      function card(p, variants) {
        const variant = variants.find(v => v.available) || variants[0];
        const el = node('article', 'lvn-shoe-card');
        el.dataset.productId = p.id;
        el.dataset.variantId = variant.id;
        el.dataset.size = variant.size;
        el.dataset.available = String(variant.available);
        const url = new URL(p.url, location.origin);
        url.searchParams.set('variant', variant.id);
        const media = node('a', 'lvn-shoe-image');
        media.href = url.pathname + url.search;
        media.setAttribute('aria-label', p.title);
        if (p.image) {
          const image = node('img'); image.src = p.image; image.alt = p.title;
          image.width = 720; image.height = 720; image.loading = 'lazy'; media.append(image);
        }
        if (!variant.available) media.append(node('span', 'lvn-shoe-badge', section.dataset.soldOut));
        const title = node('h3'), link = node('a', '', p.title);
        link.href = media.href; title.append(link);
        el.append(media, node('p', 'lvn-shoe-vendor', p.vendor), title, node('p', 'lvn-shoe-price', variant.money));
        const labels = sizeSelect.value ? variant.title : sorted(variants.map(v => v.size)).join(' · ');
        el.append(node('p', 'lvn-shoe-variant', `${section.dataset.sizeLabel}: ${labels}`));
        return el;
      }
      function render() {
        let matches = products.map(p => ({ p, variants: p.variants.filter(v => (!sizeSelect.value || v.size === sizeSelect.value) && (!stock.checked || v.available)) })).filter(m => m.variants.length);
        const price = m => (m.variants.find(v => v.available) || m.variants[0]).price;
        if (sort.value === 'price-ascending') matches.sort((a, b) => price(a) - price(b));
        if (sort.value === 'price-descending') matches.sort((a, b) => price(b) - price(a));
        if (sort.value === 'title-ascending') matches.sort((a, b) => collator.compare(a.p.title, b.p.title));
        const pages = Math.max(1, Math.ceil(matches.length / pageSize));
        page = Math.min(page, pages);
        const fragment = document.createDocumentFragment();
        matches.slice((page - 1) * pageSize, page * pageSize).forEach(m => fragment.append(card(m.p, m.variants)));
        grid.replaceChildren(fragment);
        grid.setAttribute('aria-busy', 'false');
        status.textContent = matches.length ? `${matches.length} ${section.dataset.productLabel}${sizeSelect.value ? ` · ${section.dataset.sizeLabel} ${sizeSelect.value}` : ''}` : section.dataset.empty;
        find('paging').hidden = pages <= 1;
        find('page').textContent = `${page} / ${pages}`;
        find('prev').disabled = page === 1;
        find('next').disabled = page === pages;
      }
      [sizeSelect, stock, sort].forEach(control => control.addEventListener('change', () => { page = 1; render(); writeState(); }));
      find('clear').addEventListener('click', () => { sizeSelect.value = ''; stock.checked = false; sort.value = 'featured'; page = 1; render(); writeState(); });
      ['prev', 'next'].forEach(direction => find(direction).addEventListener('click', () => {
        page += direction === 'next' ? 1 : -1; render(); writeState();
        status.scrollIntoView({ block: 'start', behavior: 'instant' });
      }));
      window.addEventListener('popstate', () => { if (section.isConnected) { readState(); render(); } });
      readState(); render();
      find('controls').hidden = false;
      section.dataset.shoeReady = 'true';
    } catch (error) {
      section.dataset.shoeReady = '';
      status.textContent = section.dataset.error;
      grid.setAttribute('aria-busy', 'false');
      retry.hidden = false;
      retry.onclick = () => browser(section);
    }
  }
  function init(scope = document) {
    scope.querySelectorAll('[data-lvn-footwear-source]').forEach(menu);
    scope.querySelectorAll('[data-lvn-shoes]').forEach(browser);
  }
  window.LVNFootwear = { init };
  document.addEventListener('shopify:section:load', event => init(event.target));
  init();
})();
